import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardCheck, Plus, Building2, Calendar, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const VAL_PILL = {
  eccellente: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  buono: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  critico: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
};
const STATO_LABEL = { pianificato: "Pianificato", in_corso: "In corso", completato: "Completato" };

export default function Ispezione() {
  const navigate = useNavigate();
  const [ispezioni, setAudits] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [tab, setTab] = useState("pianificato");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([api.get("/audits"), api.get("/hotels")]);
      setAudits(a.data || []);
      setHotels(h.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (tab === "in_corso") return ispezioni.filter((a) => a.stato === "in_corso");
    if (tab === "pianificato") return ispezioni.filter((a) => a.stato === "pianificato");
    return ispezioni.filter((a) => a.stato === "completato");
  }, [ispezioni, tab]);

  const hotelName = (id) => hotels.find((h) => h.id === id)?.nome || "—";

  const createAudit = async (hotel_id) => {
    try {
      const { data } = await api.post("/audits", { hotel_id });
      toast.success("Ispezione creata");
      navigate(`/ispezioni/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="audit-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-2"><ClipboardCheck className="h-3 w-3" /> Ispezione Capo Area · 60gg</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Ispezioni <span className="font-medium">strutture</span></h1>
          <p className="text-muted-foreground mt-1 text-sm">{loading ? "Caricamento…" : `${ispezioni.length} ispezioni totali`}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {hotels.map((h) => (
            <Button key={h.id} variant="outline" size="sm" onClick={() => createAudit(h.id)} data-testid={`new-audit-${h.id}`}>
              <Plus className="h-3 w-3 mr-1" /> {h.nome}
            </Button>
          ))}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="audit-tabs">
          <TabsTrigger value="pianificato">Pianificate ({ispezioni.filter((a) => a.stato === "pianificato").length})</TabsTrigger>
          <TabsTrigger value="in_corso">In corso ({ispezioni.filter((a) => a.stato === "in_corso").length})</TabsTrigger>
          <TabsTrigger value="completato">Completate ({ispezioni.filter((a) => a.stato === "completato").length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-2" data-testid="audit-skeleton">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-20 rounded-2xl bg-muted/40 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="ih-empty-state"><div className="ih-empty-state-icon"><svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div><h3 className="text-lg font-semibold mb-1">Nessuna ispezione</h3><p className="text-sm text-muted-foreground max-w-md">Le ispezioni verranno generate automaticamente ogni 60 giorni per ogni hotel attivo, dallo scheduler operativo.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a) => (
            <div key={a.id} data-testid={`audit-card-${a.id}`} onClick={() => navigate(`/ispezioni/${a.id}`)} className="ih-glass rounded-2xl p-5 ih-card-hover cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{STATO_LABEL[a.stato]}</div>
                {a.valutazione_complessiva && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${VAL_PILL[a.valutazione_complessiva]}`}>{a.valutazione_complessiva}</span>}
              </div>
              <h3 className="font-display text-lg font-semibold mb-2 flex items-center gap-1.5"><Building2 className="h-4 w-4 text-muted-foreground" /> {hotelName(a.hotel_id)}</h3>
              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(a.data_creazione).toLocaleDateString("it-IT")}</span>
                {a.punteggio_totale != null && <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> <strong className="text-foreground">{a.punteggio_totale}/100</strong></span>}
              </div>
              {a.stato !== "completato" && (
                <Button size="sm" className="mt-3 w-full">Compila</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
